// Google Authentication Service
import { apiService } from './api';

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture: string;
}

class AuthService {
  private currentUser: GoogleUser | null = null;
  private isInitialized: boolean = false;

  /**
   * Initialize Google Sign-In
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Wait for Google Identity Services to load
      const checkGoogleLoaded = setInterval(() => {
        if (typeof (window as any).google !== 'undefined') {
          clearInterval(checkGoogleLoaded);
          this.isInitialized = true;
          
          // Check if user is already logged in from session storage
          const savedUser = sessionStorage.getItem('google_user');
          if (savedUser) {
            this.currentUser = JSON.parse(savedUser);
          }
          
          resolve();
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogleLoaded);
        if (!this.isInitialized) {
          reject(new Error('Google Sign-In failed to load'));
        }
      }, 10000);
    });
  }

  /**
   * Show Google Sign-In prompt
   */
  async signIn(): Promise<GoogleUser> {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        reject(new Error('Auth service not initialized'));
        return;
      }

      const client = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '334400653858-hnngels93prtcrslr06a5um4kcsve7gp.apps.googleusercontent.com',
        scope: 'email profile',
        callback: async (response: any) => {
          if (response.error) {
            // Log failed attempt (no email available at this point)
            apiService.logUser('unknown', 'unknown', 'LOGIN_FAILED', response.error);
            reject(new Error(response.error));
            return;
          }

          try {
            // Get user info using the access token
            const userInfoResponse = await fetch(
              `https://www.googleapis.com/oauth2/v3/userinfo?access_token=${response.access_token}`
            );
            
            if (!userInfoResponse.ok) {
              throw new Error('Failed to get user info');
            }

            const userInfo = await userInfoResponse.json();
            
            const user: GoogleUser = {
              id: userInfo.sub,
              email: userInfo.email,
              name: userInfo.name,
              picture: userInfo.picture
            };

            this.currentUser = user;
            sessionStorage.setItem('google_user', JSON.stringify(user));
            sessionStorage.setItem('google_token', response.access_token);

            // Log successful login
            apiService.logUser(user.email, user.name, 'LOGIN_SUCCESS');
            
            resolve(user);
          } catch (error) {
            apiService.logUser('unknown', 'unknown', 'LOGIN_FAILED', String(error));
            reject(error);
          }
        }
      });

      client.requestAccessToken();
    });
  }

  /**
   * Sign out
   */
  signOut(): void {
    this.currentUser = null;
    sessionStorage.removeItem('google_user');
    sessionStorage.removeItem('google_token');
    
    // Revoke Google token if available
    const token = sessionStorage.getItem('google_token');
    if (token && (window as any).google) {
      (window as any).google.accounts.oauth2.revoke(token, () => {
        console.log('Google token revoked');
      });
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Get current user
   */
  getCurrentUser(): GoogleUser | null {
    return this.currentUser;
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return sessionStorage.getItem('google_token');
  }
}

// Export singleton instance
export const authService = new AuthService();
